import { InfoModal } from '@backrs_ui/common-ui/src/atoms/InfoModal';
import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Button, Typography, Box, Theme, SxProps } from '@mui/material';

type ConfirmModalVariant = 'destructive' | 'warning';

type ConfirmModalAction = Readonly<{
  label: string;
  onClick: () => void;
  id?: string;
}>;

type ConfirmModalVariantConfig = Readonly<{
  iconName: AppIcons;
  iconColor: ColorToken;
  iconBg: ColorToken;
  primaryButtonSx?: SxProps<Theme>;
}>;

type ConfirmModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  headerText: string;
  bodyText: string;
  primaryAction: ConfirmModalAction;
  secondaryAction?: ConfirmModalAction;
  variant?: ConfirmModalVariant;
  headerInLineWithIcon?: boolean;
}>;

export const ConfirmModal = ({
  open,
  onClose,
  headerText,
  bodyText,
  primaryAction,
  secondaryAction,
  variant = 'destructive',
  headerInLineWithIcon = true,
}: ConfirmModalProps) => {
  const config = VARIANT_CONFIG[variant];

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="xs"
      headerInLineWithIcon={headerInLineWithIcon}
      featuredIcon={{
        iconName: config.iconName,
        size: 'lg',
        color: config.iconColor,
        shape: 'round',
        backgroundColor: config.iconBg,
        border: false,
      }}
      headerContent={
        <Box sx={headerContainerSx}>
          <Typography variant="text-lg-semibold">{headerText}</Typography>
          <Typography variant="text-sm-regular">{bodyText}</Typography>
        </Box>
      }
      bodyContent={
        <Box sx={buttonContainerSx}>
          {secondaryAction && (
            <Button
              id={secondaryAction.id ?? 'confirm-modal-secondary-button'}
              onClick={secondaryAction.onClick}
              hierarchy="Secondary gray"
              size="lg"
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            id={primaryAction.id ?? 'confirm-modal-primary-button'}
            onClick={primaryAction.onClick}
            hierarchy="Primary"
            size="lg"
            sx={config.primaryButtonSx}
          >
            {primaryAction.label}
          </Button>
        </Box>
      }
    />
  );
};

const headerContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignSelf: 'flex-end',
  justifyContent: 'flex-end',
});

const destructiveButtonSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Error/700'],
  backgroundColor: backrs.colors['Colors/Background/bg-error-solid'],
  color: backrs.colors['Colors/Base/White'],
  '&:hover': {
    backgroundColor: backrs.colors['Error/700'],
    transform: 'none',
    boxShadow: 'none',
  },
});

const VARIANT_CONFIG: Record<ConfirmModalVariant, ConfirmModalVariantConfig> = {
  destructive: {
    iconName: 'Trash01',
    iconColor: 'Colors/Text/text-error-primary',
    iconBg: 'Colors/Background/bg-error-secondary',
    primaryButtonSx: destructiveButtonSx,
  },
  warning: {
    iconName: 'AlertTriangle',
    iconColor: 'Colors/Foreground/fg-warning-secondary',
    iconBg: 'Colors/Background/bg-warning-secondary',
  },
};
